import { resetPasswordSchema, type ResetPasswordInput } from '@church/domain'
import { submitSafely, useResetPassword } from '@church/data'
import { faKey, faLinkSlash, faPaperPlane } from '@church/ui-icons'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Button, ErrorNote, Field, Icon, Input } from '../components/ui.tsx'

/** better-auth appends `?token=…` to the redirect link it mails out. */
export const Route = createFileRoute('/_auth/reset-password')({
  validateSearch: z.object({ token: z.string().optional() }),
  component: ResetPasswordScreen,
})

function ResetPasswordScreen() {
  const { token } = Route.useSearch()
  const navigate = useNavigate()
  const reset = useResetPassword()
  const form = useForm<ResetPasswordInput>({ resolver: zodResolver(resetPasswordSchema) })

  if (!token) {
    return (
      <div className="space-y-3 text-center">
        <Icon icon={faLinkSlash} className="mx-auto size-10 text-content-subtle" />
        <h1 className="text-xl font-semibold text-content-default">Link invalid</h1>
        <p className="text-sm text-content-muted">
          This reset link is missing its token — request a fresh one.
        </p>
        <Link
          to="/forgot-password"
          className="inline-flex items-center gap-1.5 text-sm text-content-brand hover:underline"
        >
          <Icon icon={faPaperPlane} className="size-3.5" />
          Request a new link
        </Link>
      </div>
    )
  }

  const submit = form.handleSubmit(async ({ password }) => {
    await reset.mutateAsync({ newPassword: password, token })
    await navigate({ to: '/sign-in' })
  })

  return (
    <form onSubmit={submitSafely(submit)} className="space-y-4" noValidate>
      <h1 className="text-xl font-semibold text-content-default">Choose a new password</h1>

      <Field label="New password" error={form.formState.errors.password?.message}>
        <Input type="password" autoComplete="new-password" {...form.register('password')} />
      </Field>

      <Field label="Confirm password" error={form.formState.errors.confirmPassword?.message}>
        <Input type="password" autoComplete="new-password" {...form.register('confirmPassword')} />
      </Field>

      <ErrorNote error={reset.error} />

      <Button type="submit" icon={faKey} className="w-full" disabled={reset.isPending}>
        {reset.isPending ? 'Saving…' : 'Set new password'}
      </Button>
    </form>
  )
}
